/**
 * Cache Service
 * 
 * High-level caching abstraction layer for Redis integration.
 * Provides domain-specific caching methods with automatic key management,
 * TTL handling, and fallback support.
 * 
 * Features:
 * - Automatic key namespacing
 * - Predefined TTL defaults for different data types
 * - JSON serialization/deserialization
 * - Cache invalidation patterns
 * - Fallback to direct database queries on cache failures
 * - Performance metrics tracking
 */

import redisClient from '../utils/redisClient.js';

// Cache TTL defaults (in seconds)
const TTL = {
  USER_PROFILE: 300,        // 5 minutes
  USER_SESSION: 3600,       // 1 hour
  JOB_LISTING: 600,         // 10 minutes
  JOB_SEARCH: 300,          // 5 minutes
  JOB_RECOMMENDATIONS: 600, // 10 minutes
  SKILLS_LIST: 1800,        // 30 minutes
  CONVERSATION: 300,        // 5 minutes
  UNREAD_COUNT: 60,         // 1 minute
  NOTIFICATION_PREFS: 300,  // 5 minutes
  RATE_LIMIT: 60,           // 1 minute
  CANDIDATE_PROFILE: 600,   // 10 minutes
  ANALYTICS: 900,           // 15 minutes
  USER_PREFERENCES: 1800,   // 30 minutes
  RECENTLY_VIEWED: 86400,   // 24 hours
};

// Cache version - increment when schema changes require cache invalidation
const CACHE_VERSION = 'v2';

// Cache key prefixes
const PREFIX = {
  USER_PROFILE: 'user_profile',
  USER_SESSION: 'session',
  JOB: `job_${CACHE_VERSION}`,
  JOB_SEARCH: `job_search_${CACHE_VERSION}`,
  JOB_RECOMMENDATIONS: `job_recs_${CACHE_VERSION}`,
  SKILLS: 'skills',
  CONVERSATION: `conversation_${CACHE_VERSION}`,
  UNREAD: 'unread',
  NOTIFICATION_PREFS: 'notif_prefs',
  RATE_LIMIT: 'rate_limit',
  CANDIDATE: 'candidate',
  ANALYTICS: 'analytics',
  USER_PREFERENCES: 'user_prefs',
  RECENTLY_VIEWED: 'recent_view',
};

class CacheService {
  constructor() {
    this.redis = redisClient;
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0,
    };
  }

  /**
   * Build a cache key with proper namespacing
   */
  buildKey(prefix, identifier) {
    return `${prefix}:${identifier}`;
  }

  /**
   * Get a value from cache with automatic JSON parsing
   */
  async get(key, options = {}) {
    try {
      const value = await this.redis.get(key);
      
      if (value !== null) {
        this.stats.hits++;
        
        // Auto-parse JSON if it's a string
        if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
          try {
            return JSON.parse(value);
          } catch (e) {
            return value;
          }
        }
        
        return value;
      }
      
      this.stats.misses++;
      return null;
    } catch (error) {
      console.error(`[CacheService] Failed to get ${key}:`, error);
      this.stats.misses++;
      return null;
    }
  }

  /**
   * Set a value in cache with automatic JSON serialization
   */
  async set(key, value, ttl) {
    try {
      // Auto-stringify objects and arrays
      const serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
      
      await this.redis.set(key, serializedValue, ttl);
      this.stats.sets++;
      return true;
    } catch (error) {
      // Silently fail if Redis is unavailable - the app will continue to work
      // Only log in development if it's not a known "unavailable" error
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[CacheService] Cache operation failed for ${key}:`, error?.message);
      }
      return false;
    }
  }

  /**
   * Delete a key or pattern
   */
  async invalidate(keyOrPattern) {
    try {
      if (keyOrPattern.includes('*')) {
        await this.redis.delPattern(keyOrPattern);
      } else {
        await this.redis.del(keyOrPattern);
      }
      this.stats.deletes++;
      return true;
    } catch (error) {
      // Silently fail - the app will continue to work
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[CacheService] Cache invalidation failed for ${keyOrPattern}:`, error?.message);
      }
      return false;
    }
  }

  // ==================== USER PROFILE CACHING ====================

  /**
   * Get user profile from cache
   */
  async getUserProfile(userId) {
    const key = this.buildKey(PREFIX.USER_PROFILE, userId);
    return await this.get(key);
  }

  /**
   * Cache user profile
   */
  async setUserProfile(userId, profile) {
    const key = this.buildKey(PREFIX.USER_PROFILE, userId);
    return await this.set(key, profile, TTL.USER_PROFILE);
  }

  /**
   * Invalidate user profile cache
   */
  async invalidateUserProfile(userId) {
    const key = this.buildKey(PREFIX.USER_PROFILE, userId);
    return await this.invalidate(key);
  }

  // ==================== SESSION CACHING ====================

  /**
   * Get session data from cache
   */
  async getSession(sessionId) {
    const key = this.buildKey(PREFIX.USER_SESSION, sessionId);
    return await this.get(key);
  }

  /**
   * Cache session data
   */
  async setSession(sessionId, sessionData) {
    const key = this.buildKey(PREFIX.USER_SESSION, sessionId);
    return await this.set(key, sessionData, TTL.USER_SESSION);
  }

  /**
   * Invalidate session
   */
  async invalidateSession(sessionId) {
    const key = this.buildKey(PREFIX.USER_SESSION, sessionId);
    return await this.invalidate(key);
  }

  /**
   * Invalidate all sessions for a user
   */
  async invalidateUserSessions(userId) {
    const pattern = this.buildKey(PREFIX.USER_SESSION, `*:${userId}`);
    return await this.invalidate(pattern);
  }

  // ==================== JOB CACHING ====================

  /**
   * Get job listing from cache
   */
  async getJob(jobId) {
    const key = this.buildKey(PREFIX.JOB, jobId);
    return await this.get(key);
  }

  /**
   * Cache job listing
   */
  async setJob(jobId, jobData) {
    const key = this.buildKey(PREFIX.JOB, jobId);
    return await this.set(key, jobData, TTL.JOB_LISTING);
  }

  /**
   * Invalidate job cache
   */
  async invalidateJob(jobId) {
    const key = this.buildKey(PREFIX.JOB, jobId);
    return await this.invalidate(key);
  }

  /**
   * Get job search results from cache
   */
  async getJobSearch(searchHash) {
    const key = this.buildKey(PREFIX.JOB_SEARCH, searchHash);
    return await this.get(key);
  }

  /**
   * Cache job search results
   */
  async setJobSearch(searchHash, results) {
    const key = this.buildKey(PREFIX.JOB_SEARCH, searchHash);
    return await this.set(key, results, TTL.JOB_SEARCH);
  }

  /**
   * Invalidate all job searches (when new job posted)
   */
  async invalidateJobSearches() {
    const pattern = `${PREFIX.JOB_SEARCH}:*`;
    return await this.invalidate(pattern);
  }

  // ==================== JOB RECOMMENDATIONS CACHING ====================

  /**
   * Get job recommendations for a VA
   */
  async getJobRecommendations(vaId) {
    const key = this.buildKey(PREFIX.JOB_RECOMMENDATIONS, vaId);
    return await this.get(key);
  }

  /**
   * Cache job recommendations
   */
  async setJobRecommendations(vaId, recommendations) {
    const key = this.buildKey(PREFIX.JOB_RECOMMENDATIONS, vaId);
    return await this.set(key, recommendations, TTL.JOB_RECOMMENDATIONS);
  }

  /**
   * Invalidate job recommendations for a VA (on profile update)
   */
  async invalidateJobRecommendations(vaId) {
    const key = this.buildKey(PREFIX.JOB_RECOMMENDATIONS, vaId);
    return await this.invalidate(key);
  }

  // ==================== SKILLS CACHING ====================

  /**
   * Get global skills list
   */
  async getSkillsList() {
    const key = this.buildKey(PREFIX.SKILLS, 'global');
    return await this.get(key);
  }

  /**
   * Cache global skills list
   */
  async setSkillsList(skills) {
    const key = this.buildKey(PREFIX.SKILLS, 'global');
    return await this.set(key, skills, TTL.SKILLS_LIST);
  }

  /**
   * Invalidate skills list
   */
  async invalidateSkillsList() {
    const key = this.buildKey(PREFIX.SKILLS, 'global');
    return await this.invalidate(key);
  }

  // ==================== MESSAGING CACHING ====================

  /**
   * Get conversation metadata
   */
  async getConversation(conversationId) {
    const key = this.buildKey(PREFIX.CONVERSATION, conversationId);
    return await this.get(key);
  }

  /**
   * Cache conversation metadata
   */
  async setConversation(conversationId, conversation) {
    const key = this.buildKey(PREFIX.CONVERSATION, conversationId);
    return await this.set(key, conversation, TTL.CONVERSATION);
  }

  /**
   * Invalidate conversation cache
   */
  async invalidateConversation(conversationId) {
    const key = this.buildKey(PREFIX.CONVERSATION, conversationId);
    return await this.invalidate(key);
  }

  /**
   * Get user's conversation list from cache
   */
  async getConversations(userId, limit = 6) {
    const key = this.buildKey(PREFIX.CONVERSATION, `list:${userId}:limit_${limit}`);
    return await this.get(key);
  }

  /**
   * Cache user's conversation list
   */
  async cacheConversations(userId, limit = 6, conversations, ttl = 120) {
    const key = this.buildKey(PREFIX.CONVERSATION, `list:${userId}:limit_${limit}`);
    return await this.set(key, conversations, ttl);
  }

  /**
   * Invalidate user's conversation list
   */
  async invalidateConversations(userId) {
    const key = this.buildKey(PREFIX.CONVERSATION, `list:${userId}`);
    return await this.invalidate(key);
  }

  /**
   * Get messages for a conversation
   */
  async getMessages(conversationId) {
    const key = this.buildKey(PREFIX.CONVERSATION, `messages:${conversationId}`);
    return await this.get(key);
  }

  /**
   * Cache messages for a conversation
   */
  async cacheMessages(conversationId, messages, ttl = 120) {
    const key = this.buildKey(PREFIX.CONVERSATION, `messages:${conversationId}`);
    return await this.set(key, messages, ttl);
  }

  /**
   * Invalidate messages for a conversation
   */
  async invalidateMessages(conversationId) {
    const key = this.buildKey(PREFIX.CONVERSATION, `messages:${conversationId}`);
    return await this.invalidate(key);
  }

  /**
   * Get unread message count for a user
   */
  async getUnreadCount(userId) {
    const key = this.buildKey(PREFIX.UNREAD, userId);
    const count = await this.redis.get(key);
    return count !== null ? parseInt(count, 10) : null;
  }

  /**
   * Set unread message count
   */
  async setUnreadCount(userId, count) {
    const key = this.buildKey(PREFIX.UNREAD, userId);
    return await this.redis.set(key, count.toString(), TTL.UNREAD_COUNT);
  }

  /**
   * Increment unread count
   */
  async incrementUnreadCount(userId) {
    const key = this.buildKey(PREFIX.UNREAD, userId);
    const newCount = await this.redis.incr(key);
    await this.redis.expire(key, TTL.UNREAD_COUNT);
    return newCount;
  }

  /**
   * Decrement unread count
   */
  async decrementUnreadCount(userId) {
    const key = this.buildKey(PREFIX.UNREAD, userId);
    const newCount = await this.redis.decr(key);
    if (newCount < 0) {
      await this.redis.set(key, '0', TTL.UNREAD_COUNT);
      return 0;
    }
    return newCount;
  }

  /**
   * Reset unread count
   */
  async resetUnreadCount(userId) {
    const key = this.buildKey(PREFIX.UNREAD, userId);
    return await this.redis.set(key, '0', TTL.UNREAD_COUNT);
  }

  // ==================== NOTIFICATION PREFERENCES CACHING ====================

  /**
   * Get notification preferences
   */
  async getNotificationPreferences(userId) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, userId);
    return await this.get(key);
  }

  /**
   * Cache notification preferences
   */
  async setNotificationPreferences(userId, preferences) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, userId);
    return await this.set(key, preferences, TTL.NOTIFICATION_PREFS);
  }

  /**
   * Invalidate notification preferences
   */
  async invalidateNotificationPreferences(userId) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, userId);
    return await this.invalidate(key);
  }

  /**
   * Get user's notification list from cache
   */
  async getNotifications(userId) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, `list:${userId}`);
    return await this.get(key);
  }

  /**
   * Cache user's notification list
   */
  async cacheNotifications(userId, notifications, ttl = 180) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, `list:${userId}`);
    return await this.set(key, notifications, ttl);
  }

  /**
   * Invalidate user's notification list
   */
  async invalidateNotifications(userId) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, `list:${userId}`);
    return await this.invalidate(key);
  }

  /**
   * Get unread notification count for a user
   */
  async getNotificationCount(userId) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, `count:${userId}`);
    const count = await this.redis.get(key);
    return count !== null ? parseInt(count, 10) : null;
  }

  /**
   * Set unread notification count
   */
  async setNotificationCount(userId, count) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, `count:${userId}`);
    return await this.redis.set(key, count.toString(), TTL.UNREAD_COUNT);
  }

  /**
   * Increment unread notification count
   */
  async incrementNotificationCount(userId) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, `count:${userId}`);
    const newCount = await this.redis.incr(key);
    await this.redis.expire(key, TTL.UNREAD_COUNT);
    return newCount;
  }

  /**
   * Decrement unread notification count
   */
  async decrementNotificationCount(userId) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, `count:${userId}`);
    const newCount = await this.redis.decr(key);
    if (newCount < 0) {
      await this.redis.set(key, '0', TTL.UNREAD_COUNT);
      return 0;
    }
    return newCount;
  }

  /**
   * Reset unread notification count
   */
  async resetNotificationCount(userId) {
    const key = this.buildKey(PREFIX.NOTIFICATION_PREFS, `count:${userId}`);
    return await this.redis.set(key, '0', TTL.UNREAD_COUNT);
  }

  // ==================== RATE LIMITING ====================

  /**
   * Check and increment rate limit
   * @returns {Object} { allowed: boolean, count: number, remaining: number }
   */
  async checkRateLimit(userId, action, maxRequests, windowSeconds = 60) {
    const key = this.buildKey(PREFIX.RATE_LIMIT, `${userId}:${action}`);
    
    try {
      const count = await this.redis.incr(key);
      
      // Set expiration on first request
      if (count === 1) {
        await this.redis.expire(key, windowSeconds);
      }
      
      const allowed = count <= maxRequests;
      const remaining = Math.max(0, maxRequests - count);
      
      return { allowed, count, remaining };
    } catch (error) {
      console.error('[CacheService] Rate limit check failed:', error);
      // On error, allow the request (fail open)
      return { allowed: true, count: 0, remaining: maxRequests };
    }
  }

  /**
   * Reset rate limit for a user and action
   */
  async resetRateLimit(userId, action) {
    const key = this.buildKey(PREFIX.RATE_LIMIT, `${userId}:${action}`);
    return await this.invalidate(key);
  }

  // ==================== CANDIDATE CACHING ====================

  /**
   * Get candidate profile
   */
  async getCandidateProfile(candidateId) {
    const key = this.buildKey(PREFIX.CANDIDATE, candidateId);
    return await this.get(key);
  }

  /**
   * Cache candidate profile
   */
  async setCandidateProfile(candidateId, profile) {
    const key = this.buildKey(PREFIX.CANDIDATE, candidateId);
    return await this.set(key, profile, TTL.CANDIDATE_PROFILE);
  }

  /**
   * Invalidate candidate profile
   */
  async invalidateCandidateProfile(candidateId) {
    const key = this.buildKey(PREFIX.CANDIDATE, candidateId);
    return await this.invalidate(key);
  }

  // ==================== ANALYTICS CACHING ====================

  /**
   * Get analytics data
   */
  async getAnalytics(metricKey) {
    const key = this.buildKey(PREFIX.ANALYTICS, metricKey);
    return await this.get(key);
  }

  /**
   * Cache analytics data
   */
  async setAnalytics(metricKey, data) {
    const key = this.buildKey(PREFIX.ANALYTICS, metricKey);
    return await this.set(key, data, TTL.ANALYTICS);
  }

  /**
   * Invalidate analytics data
   */
  async invalidateAnalytics(metricKey) {
    const key = this.buildKey(PREFIX.ANALYTICS, metricKey);
    return await this.invalidate(key);
  }

  /**
   * Get user analytics summary
   */
  async getUserAnalytics(userId) {
    const key = this.buildKey(PREFIX.ANALYTICS, `user:${userId}`);
    return await this.get(key);
  }

  /**
   * Cache user analytics summary
   */
  async setUserAnalytics(userId, data, ttl = TTL.ANALYTICS) {
    const key = this.buildKey(PREFIX.ANALYTICS, `user:${userId}`);
    return await this.set(key, data, ttl);
  }

  /**
   * Get application trends
   */
  async getApplicationTrends(userId, timeRange = '30d') {
    const key = this.buildKey(PREFIX.ANALYTICS, `trends:${userId}:${timeRange}`);
    return await this.get(key);
  }

  /**
   * Cache application trends
   */
  async setApplicationTrends(userId, timeRange, data, ttl = TTL.ANALYTICS) {
    const key = this.buildKey(PREFIX.ANALYTICS, `trends:${userId}:${timeRange}`);
    return await this.set(key, data, ttl);
  }

  /**
   * Get skills analytics
   */
  async getSkillsAnalytics(userId) {
    const key = this.buildKey(PREFIX.ANALYTICS, `skills:${userId}`);
    return await this.get(key);
  }

  /**
   * Cache skills analytics
   */
  async setSkillsAnalytics(userId, data, ttl = TTL.ANALYTICS) {
    const key = this.buildKey(PREFIX.ANALYTICS, `skills:${userId}`);
    return await this.set(key, data, ttl);
  }

  /**
   * Get dashboard stats
   */
  async getDashboardStats(userId, dashboardType = 'employer') {
    const key = this.buildKey(PREFIX.ANALYTICS, `dashboard:${dashboardType}:${userId}`);
    return await this.get(key);
  }

  /**
   * Cache dashboard stats
   */
  async setDashboardStats(userId, dashboardType, data, ttl = 180) {
    const key = this.buildKey(PREFIX.ANALYTICS, `dashboard:${dashboardType}:${userId}`);
    return await this.set(key, data, ttl);  // 3min TTL for dashboard stats
  }

  /**
   * Invalidate all analytics for a user
   */
  async invalidateUserAnalytics(userId) {
    // Note: This is a simplified approach. For production, consider using Redis SCAN
    // to find and delete all keys matching a pattern
    await Promise.all([
      this.invalidate(this.buildKey(PREFIX.ANALYTICS, `user:${userId}`)),
      this.invalidate(this.buildKey(PREFIX.ANALYTICS, `trends:${userId}:7d`)),
      this.invalidate(this.buildKey(PREFIX.ANALYTICS, `trends:${userId}:30d`)),
      this.invalidate(this.buildKey(PREFIX.ANALYTICS, `trends:${userId}:90d`)),
      this.invalidate(this.buildKey(PREFIX.ANALYTICS, `trends:${userId}:1y`)),
      this.invalidate(this.buildKey(PREFIX.ANALYTICS, `skills:${userId}`)),
      this.invalidate(this.buildKey(PREFIX.ANALYTICS, `dashboard:employer:${userId}`)),
      this.invalidate(this.buildKey(PREFIX.ANALYTICS, `dashboard:va:${userId}`))
    ]);
  }

  // ==================== USER PREFERENCES CACHING ====================

  /**
   * Get user preferences (filters, UI state, etc.)
   */
  async getUserPreferences(userId, prefType = 'job_search') {
    const key = this.buildKey(PREFIX.USER_PREFERENCES, `${userId}:${prefType}`);
    return await this.get(key);
  }

  /**
   * Cache user preferences
   */
  async setUserPreferences(userId, prefType = 'job_search', preferences) {
    const key = this.buildKey(PREFIX.USER_PREFERENCES, `${userId}:${prefType}`);
    return await this.set(key, preferences, TTL.USER_PREFERENCES);
  }

  /**
   * Invalidate user preferences
   */
  async invalidateUserPreferences(userId, prefType = 'job_search') {
    const key = this.buildKey(PREFIX.USER_PREFERENCES, `${userId}:${prefType}`);
    return await this.invalidate(key);
  }

  // ==================== RECENTLY VIEWED JOBS ====================

  /**
   * Get recently viewed jobs for a user
   */
  async getRecentlyViewedJobs(userId) {
    const key = this.buildKey(PREFIX.RECENTLY_VIEWED, `jobs:${userId}`);
    return await this.get(key);
  }

  /**
   * Add a job to recently viewed list (max 20 items)
   */
  async addRecentlyViewedJob(userId, jobId) {
    const key = this.buildKey(PREFIX.RECENTLY_VIEWED, `jobs:${userId}`);
    
    // Get current list
    let recentJobs = await this.get(key) || [];
    
    // Remove if already exists (to move to front)
    recentJobs = recentJobs.filter(id => id !== jobId);
    
    // Add to front
    recentJobs.unshift(jobId);
    
    // Keep only last 20
    recentJobs = recentJobs.slice(0, 20);
    
    // Save back with 24hr TTL
    return await this.set(key, recentJobs, TTL.RECENTLY_VIEWED);
  }

  /**
   * Clear recently viewed jobs for a user
   */
  async clearRecentlyViewedJobs(userId) {
    const key = this.buildKey(PREFIX.RECENTLY_VIEWED, `jobs:${userId}`);
    return await this.invalidate(key);
  }

  // ==================== UTILITIES ====================

  /**
   * Get cache statistics
   */
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? ((this.stats.hits / total) * 100).toFixed(2) : 0;
    
    return {
      ...this.stats,
      total,
      hitRate: `${hitRate}%`,
    };
  }

  /**
   * Reset cache statistics
   */
  resetStats() {
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0,
    };
  }

  /**
   * Check if Redis is available
   */
  isAvailable() {
    return this.redis.isAvailable();
  }

  /**
   * Ping Redis connection
   */
  async ping() {
    return await this.redis.ping();
  }
}

// Export singleton instance
const cacheService = new CacheService();
export default cacheService;
